import React, { ChangeEvent, FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import SubscriptionCard from "./components/SubscriptionCard";
import FormGroup from "../../components/FormGroup";
import Label from "../../components/Label";
import Select from "../../components/Select";
import Input from "../../components/Input";
import { ICorporateBuyPlansFormStateType } from "./types";
import { TenantCode } from "../../models/brand";
import { IProductLine } from "../../services/api/productLine/types";
import { corporateBuy } from "../../config/router/routes";
import { ICustomerSKUDetails } from "../../services/api/offering/types";
import { useProductLineListQuery } from "./hooks/use-product-line-query";
import { PLANS_FEATURES } from "../../constants/common";
import useBuyFormState from "./hooks/use-corporate-buy-plans-form-state";
import { selectBrandCode } from "../../store/metadata";
import { useCustomerOfferingDetailQuery } from "../../utils/buy/use-customer-offering-detail-query";
import { getOfferingDetailsList } from "../../utils/common";

import "./styles.scss";

const initialCorporateBuyPlansFormState: ICorporateBuyPlansFormStateType = {
  productLine: "",
  offering: "",
};

export const CorporateBuyPlans: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [corporateBuyFormState, setCorporateBuyFormState] = useBuyFormState(
    initialCorporateBuyPlansFormState,
  );
  const tenant = useSelector(selectBrandCode);
  const { data: productLinenList } = useProductLineListQuery(
    tenant as TenantCode,
  );
  const { data: customerOfferingDetail } = useCustomerOfferingDetailQuery(
    corporateBuyFormState.offering,
  );

  const availableProductLineList = useMemo(() => {
    if (!productLinenList) {
      return [];
    }
    return productLinenList.filter((productLine) => {
      return productLine.is_corporate;
    });
  }, [productLinenList]);

  const handleProductLineUpdate = (event: ChangeEvent<HTMLSelectElement>) => {
    setCorporateBuyFormState({
      productLine: event.target.value,
      offering: "",
    });
  };

  const handleOfferingUpdate = (event: ChangeEvent<HTMLInputElement>) => {
    setCorporateBuyFormState({ offering: event.target.value });
  };

  const onSelectPlan = (plan: ICustomerSKUDetails, index: number) => {
    history.push(corporateBuy.path, {
      plan,
      index,
      offeringCode: corporateBuyFormState.offering,
      tenantCode: tenant,
    });
  };

  const offeringListWithGeoCurrency = useMemo(() => {
    if (!customerOfferingDetail) {
      return [];
    }

    return getOfferingDetailsList(customerOfferingDetail);
  }, [customerOfferingDetail]);

  return (
    <section className="ams-corporate-buy-plans-page">
      <header className="ams-corporate-buy-plans-page__header">
        <h1 className="page-title">{t("signUpForBusiness")}</h1>
      </header>

      <div className="ams-corporate-buy-plans-page__content">
        <section className="ams-corporate-buy-plans-page__tenant-form">
          <FormGroup>
            <Label>{t("productLine")}</Label>
            <Select
              id="buy-page-product-iine-select"
              value={corporateBuyFormState.productLine}
              required
              onChange={handleProductLineUpdate}
            >
              <option value="">{t("chooseProductLine")}</option>
              {availableProductLineList.map(({ code }: IProductLine) => {
                return (
                  <option key={code} value={code}>
                    {code}
                  </option>
                );
              })}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>{t("offering")}</Label>
            <Input
              type="text"
              value={corporateBuyFormState.offering}
              required
              onChange={handleOfferingUpdate}
            />
          </FormGroup>
        </section>

        {offeringListWithGeoCurrency.length > 0 && (
          <section className="ams-corporate-buy-plans-page__plan-details">
            <h2 className="section-title">{t("chooseYourPlan")}</h2>
          </section>
        )}

        {offeringListWithGeoCurrency.map(
          (item: ICustomerSKUDetails, index: number) => {
            return (
              <SubscriptionCard
                isCanSelectPlan
                key={item.sku_id}
                data={item}
                onSelect={() => {
                  return onSelectPlan(item, index);
                }}
                selectedLabel={t("selectedPlan")}
                features={PLANS_FEATURES[index]}
              />
            );
          },
        )}
      </div>
    </section>
  );
};
